import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  noindex = false,
  jsonLd,
}: SEOProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const defaultImage = `${siteUrl}/og-image.jpg`;

  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags
    const metaTags: Record<string, string> = {
      description,
      'og:title': title,
      'og:description': description,
      'og:url': fullCanonical,
      'og:type': 'website',
      'og:image': ogImage || defaultImage,
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': ogImage || defaultImage,
    };

    if (keywords) {
      metaTags.keywords = keywords;
    }

    if (noindex) {
      metaTags.robots = 'noindex, nofollow';
    }

    // Update or create meta tags
    Object.entries(metaTags).forEach(([name, content]) => {
      const property = name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name';
      let meta = document.querySelector(`meta[${property}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(property, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    });

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullCanonical);

    // JSON-LD
    if (jsonLd) {
      const scripts = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      const scriptIds: string[] = [];

      scripts.forEach((schema, index) => {
        const scriptId = `jsonld-${index}`;
        scriptIds.push(scriptId);
        
        let script = document.getElementById(scriptId);
        if (!script) {
          script = document.createElement('script');
          script.id = scriptId;
          script.type = 'application/ld+json';
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(schema);
      });

      // Cleanup old JSON-LD scripts
      return () => {
        scriptIds.forEach(id => {
          const script = document.getElementById(id);
          if (script) {
            script.remove();
          }
        });
      };
    }
  }, [title, description, keywords, fullCanonical, ogImage, noindex, jsonLd, siteUrl, defaultImage]);

  return null;
}